import { useEffect, useMemo, useState } from 'react';
import { ShoppingCart, Loader2, Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { fmtMoney } from '@/lib/format';
import { hapticNotify } from '@/lib/telegram';
import { cn } from '@/lib/cn';

interface Lot {
  id: string;
  brand?: string | null;
  color: string;
  length_cm: number;
  structure?: string | null;
  purchase_price_per_gram: number;
  current_grams: number;
}

interface Props {
  appointmentId: string;
  /** Pre-fill: total grams to consume (defaults to appointment.hair_grams). */
  defaultGrams?: number;
  /** Pre-fill: required minimum length. */
  preferredLengthCm?: number;
  /** Already written-off grams for this appointment (to show what's left). */
  alreadyWrittenOff?: number;
  trigger?: React.ReactNode;
  /** If true, auto-opens on mount once. */
  autoOpen?: boolean;
  onDone?: () => void;
}

export const StockWriteoffDialog = ({
  appointmentId,
  defaultGrams,
  preferredLengthCm,
  alreadyWrittenOff = 0,
  trigger,
  autoOpen,
  onDone,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [lots, setLots] = useState<Lot[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picks, setPicks] = useState<Record<string, number>>({});
  const qc = useQueryClient();

  const remaining = Math.max(0, (defaultGrams ?? 0) - alreadyWrittenOff);

  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    api
      .get('/hair-stock/suggest', {
        params: { length_cm: preferredLengthCm, grams: defaultGrams },
      })
      .then(({ data }) => setLots(data))
      .catch((e) => setError(e?.response?.data?.error ?? 'Ошибка загрузки'))
      .finally(() => setLoading(false));
  }, [open, preferredLengthCm, defaultGrams]);

  // Auto-suggest the best matching lot when lots load
  useEffect(() => {
    if (!lots.length || Object.keys(picks).length || remaining <= 0) return;
    const exact = lots.find((l) => l.length_cm === preferredLengthCm && l.current_grams >= remaining);
    if (exact) {
      setPicks({ [exact.id]: remaining });
      return;
    }
    const longer = lots.find((l) => l.length_cm >= (preferredLengthCm ?? 0) && l.current_grams >= remaining);
    if (longer) setPicks({ [longer.id]: remaining });
  }, [lots, remaining, preferredLengthCm]); // eslint-disable-line

  const totalPicked = useMemo(
    () => Object.values(picks).reduce((s, v) => s + (Number(v) || 0), 0),
    [picks],
  );

  const submit = async () => {
    setError(null);
    const ops = Object.entries(picks).filter(([, g]) => g > 0);
    if (!ops.length) {
      setError('Укажите граммы для списания');
      return;
    }
    setSubmitting(true);
    try {
      for (const [lotId, grams] of ops) {
        await api.post(`/hair-stock/${lotId}/movements`, {
          type: 'out',
          grams,
          appointment_id: appointmentId,
          note: 'Списание при процедуре',
        });
      }
      hapticNotify('success');
      qc.invalidateQueries({ queryKey: ['stock'] });
      qc.invalidateQueries({ queryKey: ['appointment', appointmentId] });
      setOpen(false);
      setPicks({});
      onDone?.();
    } catch (e: any) {
      hapticNotify('error');
      setError(e?.response?.data?.error ?? 'Ошибка списания');
    } finally {
      setSubmitting(false);
    }
  };

  const setPick = (id: string, grams: number) => {
    setPicks((p) => {
      const next = { ...p };
      if (grams <= 0) delete next[id];
      else next[id] = grams;
      return next;
    });
  };

  const balance = remaining - totalPicked;

  // Reset transient state when the dialog closes so reopening shows fresh data
  // (e.g. so previously-typed grams aren't kept around when rejected).
  useEffect(() => {
    if (!open) {
      setPicks({});
      setError(null);
      setLots([]);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" className="w-full">
            <ShoppingCart className="h-4 w-4" /> Списать материалы
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Списание волос</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Card className="border-primary/40 bg-primary/5">
            <CardContent className="space-y-1 pt-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Нужно списать</span>
                <span className="font-semibold">{remaining.toFixed(0)} г</span>
              </div>
              {alreadyWrittenOff > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Уже списано</span>
                  <span>{alreadyWrittenOff.toFixed(0)} г</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Выбрано</span>
                <span className={cn('font-semibold', balance < 0 && 'text-destructive')}>
                  {totalPicked.toFixed(0)} г
                </span>
              </div>
            </CardContent>
          </Card>

          {loading && (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && !lots.length && (
            <div className="rounded-xl border bg-muted/40 p-4 text-center text-sm text-muted-foreground">
              Подходящих партий нет. Можно завершить процедуру без списания.
            </div>
          )}

          <div className="space-y-2">
            {lots.map((l) => {
              const picked = picks[l.id] ?? 0;
              const picks_match = preferredLengthCm && l.length_cm === preferredLengthCm;
              return (
                <Card
                  key={l.id}
                  className={cn(
                    'transition-colors',
                    picked > 0 && 'border-primary',
                    picks_match && !picked && 'border-primary/40',
                  )}
                >
                  <CardContent className="space-y-2 p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium">
                          {l.color}, {l.length_cm} см
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {l.brand ?? '—'} · {fmtMoney(l.purchase_price_per_gram)}/срез
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold">{l.current_grams.toFixed(0)} г</div>
                        <div className="text-xs text-muted-foreground">остаток</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        max={l.current_grams}
                        step={0.1}
                        inputMode="decimal"
                        placeholder="Граммы"
                        value={picked || ''}
                        onChange={(e) => setPick(l.id, Number(e.target.value))}
                        className="h-9"
                      />
                      <Button
                        variant={picked > 0 ? 'default' : 'outline'}
                        size="sm"
                        onClick={() =>
                          setPick(
                            l.id,
                            picked > 0 ? 0 : Math.min(l.current_grams, remaining - totalPicked + picked),
                          )
                        }
                      >
                        {picked > 0 ? <Check className="h-4 w-4" /> : 'Всё'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {error && (
            <div className="rounded-lg bg-destructive/10 p-2 text-sm text-destructive">{error}</div>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>
              Отмена
            </Button>
            <Button
              className="flex-1"
              onClick={submit}
              disabled={submitting || totalPicked <= 0}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Списать {totalPicked > 0 && `${totalPicked.toFixed(0)} г`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
